/**
 * Service for comment management business logic
 */

export interface CommentEditState {
  [commentId: string]: boolean;
}

export interface CommentTextState {
  [commentId: string]: string;
}

export interface CommentLoadingState {
  [commentId: string]: boolean;
}

export interface CommentValidationResult {
  isValid: boolean;
  error?: string;
}

class CommentService {
  /**
   * Validate comment text before submission
   */
  validateComment(text: string): CommentValidationResult {
    const trimmedText = text.trim();

    if (!trimmedText) {
      return {
        isValid: false,
        error: 'Comment cannot be empty',
      };
    }

    return { isValid: true };
  }

  /**
   * Check if click target should cancel edit mode
   */
  shouldCancelEditOnClick(target: HTMLElement): boolean {
    // Don't cancel if we clicked on a textarea or inside an edit container
    if (
      target.tagName === 'TEXTAREA' ||
      target.closest('[data-edit-container]') ||
      target.closest('button')
    ) {
      return false;
    }

    return true;
  }

  /**
   * Get active edit comment IDs
   */
  getActiveEditIds(editingState: CommentEditState): string[] {
    return Object.keys(editingState).filter(
      commentId => editingState[commentId]
    );
  }

  /**
   * Update editing state for a comment
   */
  updateEditingState(
    currentState: CommentEditState,
    commentId: string,
    isEditing: boolean
  ): CommentEditState {
    return { ...currentState, [commentId]: isEditing };
  }

  /**
   * Update comment text state
   */
  updateCommentTextState(
    currentState: CommentTextState,
    commentId: string,
    text: string
  ): CommentTextState {
    return { ...currentState, [commentId]: text };
  }

  /**
   * Update loading state for a comment
   */
  updateLoadingState(
    currentState: CommentLoadingState,
    commentId: string,
    isLoading: boolean
  ): CommentLoadingState {
    return { ...currentState, [commentId]: isLoading };
  }

  /**
   * Clear edit state for a comment
   */
  clearEditState(
    editingState: CommentEditState,
    textState: CommentTextState,
    commentId: string
  ): { editingState: CommentEditState; textState: CommentTextState } {
    return {
      editingState: { ...editingState, [commentId]: false },
      textState: { ...textState, [commentId]: '' },
    };
  }

  /**
   * Clear all active edits
   */
  clearAllEdits(
    editingState: CommentEditState,
    textState: CommentTextState
  ): { editingState: CommentEditState; textState: CommentTextState } {
    const activeEditIds = this.getActiveEditIds(editingState);

    const newEditingState = { ...editingState };
    const newTextState = { ...textState };

    activeEditIds.forEach(commentId => {
      newEditingState[commentId] = false;
      newTextState[commentId] = '';
    });

    return {
      editingState: newEditingState,
      textState: newTextState,
    };
  }

  /**
   * Handle keyboard shortcuts for comments
   */
  handleKeyboardEvent(event: React.KeyboardEvent, action: () => void): void {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      action();
    }
  }

  /**
   * Get comment text for editing
   */
  getEditText(textState: CommentTextState, commentId: string): string {
    return textState[commentId] || '';
  }

  /**
   * Check if comment is being edited
   */
  isEditing(editingState: CommentEditState, commentId: string): boolean {
    return editingState[commentId] || false;
  }

  /**
   * Check if comment is being deleted
   */
  isDeleting(deletingState: CommentLoadingState, commentId: string): boolean {
    return deletingState[commentId] || false;
  }

  /**
   * Prepare comment for submission
   */
  prepareCommentForSubmission(text: string): string {
    return text.trim();
  }

  /**
   * Initialize edit state for a comment
   */
  initializeEditState(
    editingState: CommentEditState,
    textState: CommentTextState,
    commentId: string,
    currentText: string
  ): { editingState: CommentEditState; textState: CommentTextState } {
    return {
      editingState: { ...editingState, [commentId]: true },
      textState: { ...textState, [commentId]: currentText },
    };
  }
}

export const commentService = new CommentService();
